import { useCallback, useMemo, useState, lazy, Suspense } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Code2 } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../common/hooks"
import { PageHeader, APIKeyModal, ApprovedUrlsModal } from "../../common/ui"
import {
  IntegrationsTable,
  IntegrationsPageHeaderActions
} from "../../common/ui/integrations"
import {
  setIsSubmitting,
  setPreviewModal
} from "../../redux/slices/integrations"
import { selectUserDetails, selectUserRoles } from "../../redux/slices/auth"
import { setVisiblePanel } from "../../redux/slices/app"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { RequireRole } from "../../common/validation"
import { checkIsAdmin, checkIsSuperAdmin } from "../../common/utils"
import ManageIntegration from "../../common/ui/integrations/manage"
import { useIntegrationsPageData } from "./use-integrations-page-data"

const PreviewModal = lazy(() => import("../../common/ui/preview-modal"))

const IntegrationsPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useAppDispatch()

  const userRoles = useAppSelector(selectUserRoles)
  const user = useAppSelector(selectUserDetails)

  const isAdmin = useMemo(() => checkIsAdmin(userRoles), [userRoles])
  const isSuperAdmin = useMemo(() => checkIsSuperAdmin(userRoles), [userRoles])

  const {
    brands,
    integrations,
    integration,
    statusData,
    isLoadingIntegrations,
    isLoadingIntegration,
    isLoadingStatus
  } = useIntegrationsPageData({ id, isSuperAdmin, orgId: user?.org?.id })

  const [apiModalOpen, setAPIModalOpen] = useState(false)
  const [approvedUrlsModalOpen, setApprovedUrlsModalOpen] = useState(false)

  const handleSelectIntegration = useCallback(
    (nextId: string) => navigate(`/integrations/${nextId}`),
    [navigate]
  )

  const clearSelectedIntegration = useCallback(() => {
    navigate("/integrations")
    dispatch(setIsSubmitting(false))
  }, [dispatch, navigate])

  const onClickNew = useCallback(() => {
    dispatch(setVisiblePanel("integration/new"))
  }, [dispatch])

  const onOpenApprovedUrls = useCallback(
    () => setApprovedUrlsModalOpen(true),
    []
  )

  const onOpenApiKey = useCallback(() => setAPIModalOpen(true), [])

  const onClosePreviewModal = useCallback(() => {
    dispatch(setPreviewModal({ isOpen: false, appKey: "" }))
  }, [dispatch])

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.integrations}>
      <>
        <div className="min-h-full p-6 bg-white">
          <PageHeader title="Integrations">
            <IntegrationsPageHeaderActions
              isAdmin={isAdmin}
              onOpenApprovedUrls={onOpenApprovedUrls}
              onOpenApiKey={onOpenApiKey}
              onClickNew={onClickNew}
            />
          </PageHeader>

          <IntegrationsTable
            integrations={integrations}
            brands={brands}
            isLoading={isLoadingIntegrations}
            onSelect={handleSelectIntegration}
            emptyStateProps={{
              heading: "Add your first integration",
              subHeading: "",
              icon: Code2,
              isActionButton: true,
              buttonText: "Add new integration",
              onClick: onClickNew
            }}
          />
        </div>

        <APIKeyModal
          show={apiModalOpen}
          toggleModal={() => setAPIModalOpen(false)}
        />

        <ApprovedUrlsModal
          show={approvedUrlsModalOpen}
          toggleModal={setApprovedUrlsModalOpen}
        />

        <Suspense fallback={<div>Loading...</div>}>
          <PreviewModal onClose={onClosePreviewModal} />
        </Suspense>

        <ManageIntegration
          open={!!id}
          isCreate={false}
          integration={integration}
          statusData={statusData}
          isLoadingStatus={isLoadingStatus}
          isLoadingIntegration={isLoadingIntegration}
          onClose={clearSelectedIntegration}
        />
      </>
    </RequireRole>
  )
}

export default IntegrationsPage
